import React, { useCallback, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "components/common/TableContainer";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import {
  cancelPettyWork,
  deletePettyWork,
  getPettyWorks,
  holdPettyWork,
  resumePettyWork,
} from "features/PettyWorks/services/PettyWorkService";
import { runPettyWorkStart } from "features/PettyWorks/pettyWorkWorkflow";
import StartPettyWorkModal from "features/PettyWorks/StartPettyWorkModal";
import StatusBadge from "components/common/StatusBadge";
import { PettyWork } from "features/PettyWorks/types/petty-work.types";
import { priorityMap } from "features/Meta/constants/meta.constants";
import { CodeTitleColumns, IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import {
  PettyWorkStatus,
  pettyWorkStatusMap,
} from "features/PettyWorks/constants/petty-work.constants";
import CancelModal from "../CancelModal";

import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import {
  canEditPettyWorkStatus,
  canReassignPettyWork,
  isTerminalPettyWorkForAssignment,
  pettyWorkAllowsMaterialRequest,
  shouldShowPettyWorkAssignmentActions,
} from "features/PettyWorks/utils/pettyWorkActionPolicy";
import PettyWorkListFilters from "./PettyWorkListFilters";
import PettyWorkAssigneesAvatarGroup from "./PettyWorkAssigneesAvatarGroup";
import { MaterialRequestFormSource } from "features/MaterialRequests/constants/material-request.constants";
import { getMaterialRequestFromJobPath } from "features/MaterialRequests/utils/materialRequestFromJobPaths";
import { getPettyWorkLifecycleVisibility } from "features/PettyWorks/utils/petty-work-lifecycle.utils";
import PettyWorkLifecycleModals, {
  type PettyWorkLifecycleIntent,
} from "features/PettyWorks/components/PettyWorkLifecycleModals";
import { invalidateReadyForBillingPettyWorkCount } from "features/PettyWorks/pettyWorkReadyForBillingCountInvalidate";


const PettyWorkList = () => {
  document.title = getDocumentTitle("Petty Works");
  const { can } = useAccess();
  const canReadPw = can(Permissions.PETTY_WORK.READ);
  const canUpdatePw = can(Permissions.PETTY_WORK.UPDATE);
  const canDeletePwPerm = can(Permissions.PETTY_WORK.DELETE);
  const canStartPw = can(Permissions.PETTY_WORK.START);
  const canCancelPwPerm = can(Permissions.PETTY_WORK.CANCEL);
  const canAssign = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canReassign = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const canCreateMaterialRequest = can(Permissions.MATERIAL_REQUEST.CREATE);

  const tableRef = useRef<() => void>(null);
  const [pettyWork, setPettyWork] = useState<PettyWork | null>(null);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormPettyWork, setAssignFormPettyWork] =
    useState<PettyWork | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [startModalRecord, setStartModalRecord] = useState<PettyWork | null>(
    null,
  );
  const [startBusy, setStartBusy] = useState(false);
  const [holdRecord, setHoldRecord] = useState<PettyWork | null>(null);
  const [holdReason, setHoldReason] = useState("");
  const [holdBusy, setHoldBusy] = useState(false);
  const [resumeRecord, setResumeRecord] = useState<PettyWork | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [lifecycleRecord, setLifecycleRecord] = useState<PettyWork | null>(null);
  const [lifecycleIntent, setLifecycleIntent] =
    useState<PettyWorkLifecycleIntent | null>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [clientIdFilter, setClientIdFilter] = useState<number | null>(null);
  const requestParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      clientId: clientIdFilter ?? undefined,
    }),
    [clientIdFilter, statusFilter],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (statusFilter) n += 1;
    if (clientIdFilter != null) n += 1;
    return n;
  }, [clientIdFilter, statusFilter]);

  const clearListFilters = useCallback(() => {
    setStatusFilter("");
    setClientIdFilter(null);
  }, []);

  const onClickDelete = useCallback((record: PettyWork) => {
    setPettyWork(record);
    setDeleteModal(true);
  }, []);

  const onClickCancel = useCallback((record: PettyWork) => {
    setPettyWork(record);
    setCancelModal(true);
  }, []);

  const handleDelete = async () => {
    if (pettyWork) {
      if (pettyWork.status !== PettyWorkStatus.NEW) {
        toast.error(
          "Only new petty work can be deleted. Approved or later records cannot be deleted.",
        );
        setDeleteModal(false);
        return;
      }
      try {
        const response = await deletePettyWork(pettyWork.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ?? "Could not delete petty work.",
        );
      }
    }
  };

  const handleCancel = async (reason: string | null) => {
    if (pettyWork) {
      try {
        const response = await cancelPettyWork(pettyWork.id, { reason });
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setCancelModal(false);
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not cancel petty work.",
        );
      }
    }
  };

  const openStartModal = useCallback((record: PettyWork) => {
    setStartModalRecord(record);
    setStartModalOpen(true);
  }, []);

  const closeStartModal = useCallback(() => {
    if (startBusy) return;
    setStartModalOpen(false);
    setStartModalRecord(null);
  }, [startBusy]);

  const handleStartConfirm = useCallback(async () => {
    if (!startModalRecord) return;
    setStartBusy(true);
    try {
      const ok = await runPettyWorkStart(startModalRecord.id);
      if (ok) {
        setStartModalOpen(false);
        setStartModalRecord(null);
        tableRef.current?.();
      }
    } finally {
      setStartBusy(false);
    }
  }, [startModalRecord]);

  const handleHoldConfirm = useCallback(async () => {
    if (!holdRecord || !holdReason.trim()) return;
    setHoldBusy(true);
    try {
      const res = await holdPettyWork(holdRecord.id, {
        reason: holdReason.trim(),
      });
      if (res.success) {
        toast.success(res.message ?? "Put on hold.");
        setHoldRecord(null);
        setHoldReason("");
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Failed to hold.");
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to hold.",
      );
    } finally {
      setHoldBusy(false);
    }
  }, [holdRecord, holdReason]);

  const handleResumeConfirm = useCallback(async () => {
    if (!resumeRecord) return;
    setResumeBusy(true);
    try {
      const res = await resumePettyWork(resumeRecord.id);
      if (res.success) {
        toast.success(res.message ?? "Resumed.");
        setResumeRecord(null);
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Failed to resume.");
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to resume.",
      );
    } finally {
      setResumeBusy(false);
    }
  }, [resumeRecord]);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormPettyWork(null);
    setAssignFormIsReassign(false);
  }, []);

  const openAssign = useCallback((record: PettyWork) => {
    setAssignFormIsReassign(false);
    setAssignFormPettyWork(record);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((record: PettyWork) => {
    if (!canReassignPettyWork(record.status)) {
      toast.info(
        "Completed, cancelled, or rejected petty work cannot be reassigned.",
      );
      return;
    }
    setAssignFormIsReassign(true);
    setAssignFormPettyWork(record);
    setAssignFormOpen(true);
  }, []);

  const columns: ColumnDef<PettyWork>[] = useMemo(
    () => [
      IndexColumn<PettyWork>(),
      ...CodeTitleColumns<PettyWork>({
        titleHeader: "Petty Work",
        accessorKey: "code",
        resolve: (r) => ({
          code: r.code,
          title: r.title?.trim(),
          to: canReadPw ? `/petty-works/${r.id}` : null,
        }),
      }),
      {
        header: "Priority",
        accessorKey: "priority.name",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <StatusBadge value={row.original.priorityId} map={priorityMap} />
          );
        },
      },

      {
        header: "Assigned",
        id: "assignedUsers",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const canAssignMore =
            shouldShowPettyWorkAssignmentActions(record.status) && canAssign;
          return (
            <PettyWorkAssigneesAvatarGroup
              pettyWork={record}
              assignments={record.assignments}
              pettyWorkId={record.id}
              canReadDetail={canReadPw}
              canAssignMore={canAssignMore}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={pettyWorkStatusMap} />;
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const d = getValue() as string | undefined;
          return d ? <DateTime date={d} /> : <span className="text-muted">—</span>;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const statusAllowsCancel = record.status === PettyWorkStatus.NEW;
          const statusAllowsDelete = record.status === PettyWorkStatus.NEW;
          const statusIsTerminal = isTerminalPettyWorkForAssignment(
            record.status,
          );
          const showStart = canStartPw && record.status === PettyWorkStatus.NEW;
          const showHold =
            canStartPw && record.status === PettyWorkStatus.IN_PROGRESS;
          const showResume =
            canStartPw && record.status === PettyWorkStatus.ON_HOLD;
          const showCancel = canCancelPwPerm && statusAllowsCancel;
          const lifecycleVis = getPettyWorkLifecycleVisibility(record, can);
          const showWorkflowItems =
            showCancel ||
            showStart ||
            showHold ||
            showResume ||
            lifecycleVis.submitCompletion ||
            lifecycleVis.approveForBilling ||
            lifecycleVis.rejectCompletion ||
            lifecycleVis.submitBilling;
          const isAssignmentActionable = shouldShowPettyWorkAssignmentActions(
            record.status,
          );
          const showAssignmentItems =
            isAssignmentActionable && (canAssign || canReassign);
          const showView = canReadPw;
          const showEdit = canUpdatePw && canEditPettyWorkStatus(record.status);
          const showDelete = canDeletePwPerm && !statusIsTerminal;
          const showRequestMaterials =
            canCreateMaterialRequest &&
            pettyWorkAllowsMaterialRequest(record.status);

          const hasHeaderRow = showView || showEdit;
          const hasAny =
            hasHeaderRow ||
            showAssignmentItems ||
            showWorkflowItems ||
            showDelete ||
            showRequestMaterials;
          if (!hasAny) {
            return <span className="text-muted small">—</span>;
          }

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Petty work actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {showView ? (
                  <DropdownItem tag={Link} to={`/petty-works/${record.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/petty-works/edit/${record.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showRequestMaterials ? (
                  <>
                    {hasHeaderRow ? <DropdownItem divider /> : null}
                    <DropdownItem
                      tag={Link}
                      to={getMaterialRequestFromJobPath(
                        MaterialRequestFormSource.PETTY_WORK,
                        record.id,
                      )}
                    >
                      <i className="ri-inbox-archive-line align-bottom me-2 text-muted" />
                      Request materials
                    </DropdownItem>
                  </>
                ) : null}
                {showAssignmentItems ? (
                  <>
                    {hasHeaderRow || showRequestMaterials ? (
                      <DropdownItem divider />
                    ) : null}
                    {canAssign ? (
                      <DropdownItem onClick={() => openAssign(record)}>
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canReassign ? (
                      <DropdownItem onClick={() => void openReassign(record)}>
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {showWorkflowItems ? (
                  <>
                    {hasHeaderRow ||
                    showAssignmentItems ||
                    showRequestMaterials ? (
                      <DropdownItem divider />
                    ) : null}
                    {showStart ? (
                      <DropdownItem onClick={() => openStartModal(record)}>
                        <i className="ri-play-circle-line align-bottom me-2 text-primary" />
                        Start work
                      </DropdownItem>
                    ) : null}
                    {showHold ? (
                      <DropdownItem
                        onClick={() => {
                          setHoldReason("");
                          setHoldRecord(record);
                        }}
                      >
                        <i className="ri-pause-circle-line align-bottom me-2 text-muted" />
                        Hold
                      </DropdownItem>
                    ) : null}
                    {showResume ? (
                      <DropdownItem onClick={() => setResumeRecord(record)}>
                        <i className="ri-play-circle-line align-bottom me-2 text-success" />
                        Resume
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.submitCompletion ? (
                      <DropdownItem
                        onClick={() => {
                          setLifecycleRecord(record);
                          setLifecycleIntent({ kind: "submitCompletion" });
                        }}
                      >
                        <i className="ri-checkbox-circle-line align-bottom me-2 text-success" />
                        Submit completion
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.approveForBilling ? (
                      <DropdownItem
                        onClick={() => {
                          setLifecycleRecord(record);
                          setLifecycleIntent({ kind: "approveForBilling" });
                        }}
                      >
                        <i className="ri-check-double-line align-bottom me-2 text-success" />
                        Approve for billing
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.submitBilling ? (
                      <DropdownItem
                        onClick={() => {
                          setLifecycleRecord(record);
                          setLifecycleIntent({ kind: "submitBilling" });
                        }}
                      >
                        <i className="ri-bill-line align-bottom me-2 text-primary" />
                        Submit billing
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.rejectCompletion ? (
                      <DropdownItem
                        className="text-danger"
                        onClick={() => {
                          setLifecycleRecord(record);
                          setLifecycleIntent({ kind: "rejectCompletion" });
                        }}
                      >
                        <i className="ri-close-circle-line align-bottom me-2" />
                        Reject completion
                      </DropdownItem>
                    ) : null}
                    {showCancel ? (
                      <DropdownItem onClick={() => onClickCancel(record)}>
                        <i className="ri-close-circle-line align-bottom me-2 text-warning" />
                        Cancel
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {showDelete ? (
                  <>
                    {(hasHeaderRow ||
                      showAssignmentItems ||
                      showWorkflowItems ||
                      showRequestMaterials) && <DropdownItem divider />}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(record)}
                      disabled={!statusAllowsDelete}
                      title={
                        !statusAllowsDelete
                          ? "Only new petty work can be deleted (not after approval)"
                          : undefined
                      }
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      can,
      canAssign,
      canReassign,
      canCreateMaterialRequest,
      canReadPw,
      canUpdatePw,
      canDeletePwPerm,
      canStartPw,
      canCancelPwPerm,
      openStartModal,
      openAssign,
      openReassign,
      onClickCancel,
      onClickDelete,
      setHoldRecord,
      setResumeRecord,
      setHoldReason,
      setLifecycleRecord,
      setLifecycleIntent,
    ],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <CancelModal
        show={cancelModal}
        onCancelClick={handleCancel}
        onCloseClick={() => setCancelModal(false)}
        record={pettyWork}
      />

      <StartPettyWorkModal
        show={startModalOpen && startModalRecord != null}
        pettyWorkLabel={
          startModalRecord?.code?.trim()
            ? startModalRecord.code.trim()
            : startModalRecord?.title?.trim() || undefined
        }
        busy={startBusy}
        onClose={closeStartModal}
        onConfirm={() => void handleStartConfirm()}
      />

      {/* Hold modal */}
      <Modal
        isOpen={holdRecord != null}
        toggle={() => !holdBusy && setHoldRecord(null)}
        centered
      >
        <ModalHeader toggle={() => !holdBusy && setHoldRecord(null)}>
          Put work on hold
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            <code>{holdRecord?.code ?? `#${holdRecord?.id}`}</code> will move to{" "}
            <strong>On hold</strong>.
          </p>
          <Label className="form-label">
            Reason <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Explain why the work is being paused…"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            disabled={holdBusy}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={holdBusy}
            onClick={() => setHoldRecord(null)}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={holdBusy || !holdReason.trim()}
            onClick={() => void handleHoldConfirm()}
          >
            {holdBusy ? "Please wait…" : "Put on hold"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Resume modal */}
      <Modal
        isOpen={resumeRecord != null}
        toggle={() => !resumeBusy && setResumeRecord(null)}
        centered
      >
        <ModalHeader toggle={() => !resumeBusy && setResumeRecord(null)}>
          Resume work
        </ModalHeader>
        <ModalBody>
          <code>{resumeRecord?.code ?? `#${resumeRecord?.id}`}</code> will move
          back to <strong>In progress</strong>.
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={resumeBusy}
            onClick={() => setResumeRecord(null)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            disabled={resumeBusy}
            onClick={() => void handleResumeConfirm()}
          >
            {resumeBusy ? "Please wait…" : "Resume"}
          </Button>
        </ModalFooter>
      </Modal>

      <PettyWorkLifecycleModals
        pettyWork={lifecycleRecord}
        intent={lifecycleRecord ? lifecycleIntent : null}
        onFinished={() => {
          setLifecycleIntent(null);
          setLifecycleRecord(null);
        }}
        onSuccess={() => {
          tableRef.current?.();
          invalidateReadyForBillingPettyWorkCount();
        }}
      />

      <WorkAssignmentForm
        show={assignFormOpen && assignFormPettyWork != null}
        sourceType={WorkAssignmentSourceType.PETTY_WORK}
        sourceId={assignFormPettyWork?.id ?? 0}
        sourceLabel={
          assignFormPettyWork
            ? assignFormPettyWork.code
              ? `Petty work ${assignFormPettyWork.code}`
              : assignFormPettyWork.title
                ? `Petty work: ${assignFormPettyWork.title}`
                : `Petty work #${assignFormPettyWork.id}`
            : undefined
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={() => {
          tableRef.current?.();
        }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Petty Works" pageTitle="Petty Works" />
          <Card>
            <CardHeader className="d-flex justify-content-between">
              <h5 className="card-title mb-0">Petty Work List</h5>
              {can(Permissions.PETTY_WORK.CREATE) ? (
                <Link to="/petty-works/create" className="btn btn-primary">
                  <i className="ri-add-line align-bottom me-1"></i> Add Petty
                  Work
                </Link>
              ) : null}
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getPettyWorks}
                requestParams={requestParams}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                toolbarRight={
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <PettyWorkListFilters
                      status={statusFilter}
                      onStatusChange={setStatusFilter}
                      clientId={clientIdFilter}
                      onClientIdChange={setClientIdFilter}
                    />
                    {filtersBadgeCount > 0 && (
                      <button
                        type="button"
                        className="btn btn-soft-danger d-flex align-items-center gap-1"
                        onClick={clearListFilters}
                      >
                        <i className="ri-close-circle-line" />
                        Clear
                      </button>
                    )}
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PettyWorkList;
